import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useState, useContext, useRef, useMemo } from 'react'
import { TextField, Alert, Tooltip, Typography, CircularProgress, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { Sidebar } from 'components/sidedrawer/Sidebar2'
import { useApi } from 'auth/useApi3'
import { IApiFunc, IApiResult } from 'auth/interface/api.interface'
import { AppContext } from 'components/AppContextProvider'
import { apiEndpoints } from 'generated/apiEndpoints'
import {
  InspectionBookingSidebarProps,
  InspectionBookingFormValues,
  CreateInspectionBookingDto,
  UpdateInspectionBookingDto,
  InspectionBookingData
} from './InspectionBookingSidebar.interfaces'
import { InspectionBookingValidationSchema } from './InspectionBookingSidebar.validation'
import {
  StyledContainer,
  StyledInspectionInfoBox,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
  StyledWarningBox,
  StyledWarningText,
  StyledFormSection,
  StyledSectionTitle,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails
} from './InspectionBookingSidebar.styled'
import { faCalendarDay, faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTimePicker } from '@mui/x-date-pickers'
import { formatDateTimeForApi, showDate } from 'components/DateHandler/dateHandler'
import { StyledRowGapBox } from 'shared/styled/General'
import { StyledInputLabel } from 'components/Form/FormComponents/FormSwitch/FormSwitch.styled'
import theme from "css/theme/light";

const BookingDateTextField: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<InspectionBookingFormValues>()
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <Box>
      <StyledRowGapBox sx={{ gap: 1 }}>
        <StyledInputLabel>
          <Typography variant="h6" color="text.primary">
            {t("checks.date_for_inspection", "Date of booked inspection")}
          </Typography>
        </StyledInputLabel>
        <Tooltip
          title={
            t("checks.date_desc", "The inspection date can only be changed up to and including the day of booking. Once the day has passed, it is no longer possible to adjust the date.")
          }
        >
          <FontAwesomeIcon
            icon={faCircleInfo}
            color={theme.palette["primary"].dark}
          />
        </Tooltip>
      </StyledRowGapBox>
      <DateTimePicker
        disablePast
        format="YYYY-MM-DD HH:mm"
        ampm={false}
        value={values.bookingDate ? moment(values.bookingDate) : null}
        onChange={(newValue) => {
          setFieldValue("bookingDate", newValue ? newValue.toDate() : null)
        }}
        slotProps={{
          textField: {
            fullWidth: true,
          }
        }}
      />
    </Box>
  )
}

const RemarkTextField: React.FC = () => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<InspectionBookingFormValues>()
  const { t } = useTranslation()

  return (
    <Box>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">
          {t('global.notes', 'Notes')}
        </Typography>
      </StyledInputLabel>
      <TextField
        fullWidth
        size="small"
        multiline
        rows={3}
        name="remark"
        value={values.remark || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.remark && Boolean(errors.remark)}
        helperText={touched.remark && errors.remark}
      />
    </Box>
  )
}

interface InspectionInfoBoxProps {
  service: any | null
  isEditMode: boolean
  isCompleted: boolean
}

const InspectionInfoBox: React.FC<InspectionInfoBoxProps> = ({ service, isEditMode, isCompleted }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  if (!service) return null

  const isOverdue = service.isOverdue
  const dueDate = service.nextValue ? showDate(service.nextValue) : 'Not set'
  const daysUntil = service.daysUntilService

  return (
    <StyledInspectionInfoBox>
      <StyledRowGapBox sx={{ gap: 1, mb: 1 }}>
        <StyledSectionTitle variant="h6">
          {t('checks.vehicle_information', 'Vehicle Information')}
        </StyledSectionTitle>
        {!isEditMode && !isCompleted && (
          <Tooltip
            title={
              t("checks.inspection_booking_info", "This action registers a scheduled inspection date in the system. No booking is sent to inspection companies. When the inspection is later confirmed via the Swedish Transport Agency, the status is automatically updated and the next inspection date is moved forward one year.")
            }
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              color={theme.palette["primary"].dark}
            />
          </Tooltip>
        )}
      </StyledRowGapBox>

      <StyledInfoRow>
        <StyledInfoLabel>{t('global.vehicle', 'Vehicle')}:</StyledInfoLabel>
        <StyledInfoValue>
          {service.vehicleRegNumber} {service.vehicleInternNumber && `(${service.vehicleInternNumber})`}
        </StyledInfoValue>
      </StyledInfoRow>

      <StyledInfoRow>
        <StyledInfoLabel>{t('checks.due_date', 'Due date')}:</StyledInfoLabel>
        <StyledInfoValue sx={{
          color: isOverdue ? theme.palette.error.main : theme.palette.text.primary,
          fontWeight: isOverdue ? 'bold' : 'normal'
        }}>
          {dueDate}
        </StyledInfoValue>
      </StyledInfoRow>

      {daysUntil !== undefined && (
        <StyledInfoRow>
          <StyledInfoLabel>{t('checks.days_until_due', 'Days until due')}:</StyledInfoLabel>
          <StyledInfoValue sx={{
            color: isOverdue ? theme.palette.error.main :
              (daysUntil <= 30 ? theme.palette.warning.main : theme.palette.text.primary),
            fontWeight: isOverdue || daysUntil <= 30 ? 'bold' : 'normal'
          }}>
            {isOverdue ? `${Math.abs(daysUntil)} ${t("global.days_overdue", "days overdue")}` : `${daysUntil} ${t("global.days", "days")}`}
          </StyledInfoValue>
        </StyledInfoRow>
      )}

      {service.hasInspectionBooking && (service.vehicleCheckHistoryStatus === 'Booked' || service.vehicleCheckHistoryStatus === 'Waiting') && (
        <StyledInfoRow>
          <StyledInfoLabel>{t('checks.current_booking', 'Current booking')}:</StyledInfoLabel>
          <StyledInfoValue>
            {service.inspectionBookingDate ? showDate(service.inspectionBookingDate) : t("global.not_set", "Not set")}
          </StyledInfoValue>
        </StyledInfoRow>
      )}

      {service.hasInspectionBooking && service.vehicleCheckHistoryStatus && service.vehicleCheckHistoryStatus !== 'Waiting' && service.vehicleCheckHistoryStatus !== 'Booked' && (
        <StyledInfoRow>
          <StyledInfoLabel>{t('checks.last_booking_status', 'Last inspection result')}:</StyledInfoLabel>
          <StyledInfoValue sx={{
            color: service.vehicleCheckHistoryStatus === 'Passed' ? theme.palette.success.main : theme.palette.error.main,
            fontWeight: 'bold'
          }}>
            {service.vehicleCheckHistoryStatus}
            {service.inspectionBookingDate && ` (${showDate(service.inspectionBookingDate)})`}
          </StyledInfoValue>
        </StyledInfoRow>
      )}

      {isOverdue && (
        <StyledWarningBox>
          <StyledWarningText>
            {t('checks.inspection_overdue_warning', 'This inspection is overdue. Please book as soon as possible.')}
          </StyledWarningText>
        </StyledWarningBox>
      )}
    </StyledInspectionInfoBox>
  )
}

const formId = 'InspectionBookingSidebar'

const getFormData = (t: any, service: any) => ({
  formId: formId,
  titleNew: t('check.register_inspection', 'Book inspection'),
  titleEdit: t('checks.edit_inspection_booking', 'Edit inspection booking'),
  subTitleNew: `${t('check.book_inspection_for', 'Register inspection for')} ${service?.vehicleRegNumber || 'vehicle'}`,
  subTitleEdit: `${t('checks.edit_booking_for', 'Edit inspection booking for')} ${service?.vehicleRegNumber || 'vehicle'}`,
  submitText: t('checks.register_inspection', 'Register Inspection'),
  submitEditText: t('global.save_changes', 'Save Changes'),
  icon: faCalendarDay,
})

const bookingDesc = `When you have booked an appointment with an inspection company of your choice, you register the date here in the system, and the vehicle is then marked as <strong>"Booked".</strong><br /><br > The day after the registered date, the inspection is automatically moved to the <strong>"History"</strong> tab and remains there as pending until we receive the next weekly delivery of inspection data (Wednesday morning).<br /><br /> When we receive a response: <ul style="list-style-type:disc border-bottom: 1px solid gray padding-bottom: 18px"> <li>If the inspection is approved, the status is automatically updated to <strong>"Approved"</strong>.</li> <li>If the inspection is not approved, the case is moved back to the <strong>"Upcoming"</strong> tab, marked with a driving ban and given a new last date for the inspection. When you have booked the re-inspection, register the new date here in the system again.</li> </ul><br />`

const editBookingDesc = `When an inspection booking is already registered, the booking date can be updated if necessary.<br /><br />

The date can be changed <strong>up to and including the registered booking date.</strong> Once the day has passed, it is <strong>no longer possible to adjust the date. </strong>`

export const InspectionBookingSidebar: React.FC<InspectionBookingSidebarProps> = ({
  vehicleId,
  checkTemplateId,
  onBooked,
  onUpdated
}) => {
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const formRef = useRef<any>()
  const [inspectionBookingResult, inspectionBookinApi] = useApi() as [IApiResult, IApiFunc]
  const [inspectionDataResult, inspectionDataApi] = useApi() as [IApiResult, IApiFunc]
  const [inspection, setInspection] = useState<InspectionBookingData | null>(null)
  const [expanded, setExpanded] = useState(false);

  const isCompleted = inspection?.vehicleCheckHistoryStatus === 'Passed' || inspection?.vehicleCheckHistoryStatus === 'Failed'
  const isEditMode = inspection?.hasInspectionBooking &&
    (inspection?.vehicleCheckHistoryStatus === 'Booked' || inspection?.vehicleCheckHistoryStatus === 'Waiting')

  // Only pre-fill form with existing data if we're in edit mode (status is Booked or Waiting)
  // For completed bookings, show a fresh form for the new booking
  const initialFormValues: InspectionBookingFormValues = useMemo(() => ({
    id: isEditMode ? (inspection?.inspectionBookingId || 0) : 0,
    bookingDate: isEditMode && inspection?.inspectionBookingDate
      ? moment(inspection.inspectionBookingDate)
      : moment(),
    remark: isEditMode ? (inspection?.inspectionBookingRemark || '') : '',
  }), [isEditMode, inspection?.inspectionBookingDate, inspection?.inspectionBookingId, inspection?.inspectionBookingRemark])
  const [initialFormikValues, setInitialFormikValues] = useState(initialFormValues)

  const handleSubmit = async (values: InspectionBookingFormValues) => {
    if (!inspection) return

    if (isEditMode && inspection.inspectionBookingId) {
      const updateData: UpdateInspectionBookingDto = {
        id: inspection.inspectionBookingId,
        checkTemplateId: inspection.checkTemplateId,
        bookingDate: formatDateTimeForApi(values.bookingDate),
        remark: values.remark || '',
      }

      const url = apiEndpoints.checktemplate.updateinspectionbooking
      inspectionBookinApi.put(url, updateData)
    } else {
      const createData: CreateInspectionBookingDto = {
        vehicleId: inspection.vehicleId,
        checkTemplateId: inspection.checkTemplateId,
        bookingDate: formatDateTimeForApi(values.bookingDate),
        remark: values.remark || '',
      }
      const url = apiEndpoints.checktemplate.createinspectionbooking
      inspectionBookinApi.post(url, createData)
    }
  }

  useEffect(() => {
    if (inspectionBookingResult.status === 2) {
      if (isEditMode && onUpdated) {
        onUpdated()
      } else if (!isEditMode && onBooked) {
        onBooked()
      }
      appContext.toggleSideBar(formId, false)
    } else if (inspectionBookingResult.status === 3) {
      console.error('Failed to process inspection booking', inspectionBookingResult)
    }
  }, [inspectionBookingResult, isEditMode, onBooked, onUpdated, appContext])

  // Fetch inspection data when sidebar opens
  useEffect(() => {
    if (appContext.isSidebarOpen === formId && vehicleId && checkTemplateId) {
      const url = apiEndpoints.checktemplate.getinspectionforbooking.replace("{checkTemplateId}", checkTemplateId?.toString()).replace("{vehicleId}", vehicleId?.toString())
      inspectionDataApi.get(url)
    }
  }, [appContext.isSidebarOpen, vehicleId, checkTemplateId])

  // Handle inspection data fetch result
  useEffect(() => {
    if (inspectionDataResult.status === 2 && inspectionDataResult.data) {
      setInspection(inspectionDataResult.data)
    } else if (inspectionDataResult.status === 3) {
      console.error('Failed to fetch inspection data', inspectionDataResult)
    }
  }, [inspectionDataResult])

  useEffect(() => {
    if (appContext.isSidebarOpen === formId) {
      formRef?.current?.resetForm()
      setInitialFormikValues(initialFormValues)
    } else {
      // Reset inspection data when sidebar closes
      setInspection(null)
    }
  }, [appContext.isSidebarOpen, initialFormValues])

  useEffect(() => {
    if (!inspection) return
    setInitialFormikValues(initialFormValues)
  }, [inspection, initialFormValues])

  const formData = getFormData(t, inspection)

  // Show loading state while fetching data
  if (appContext.isSidebarOpen === formId && inspectionDataResult.status === 1) {
    return (
      <Sidebar
        initialFormikValues={initialFormikValues}
        setInitialFormikValues={setInitialFormikValues}
        formResult={inspectionBookingResult}
        formData={formData}
        handleSubmit={handleSubmit}
        validationSchema={InspectionBookingValidationSchema(t)}
        formRef={formRef}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Sidebar>
    )
  }

  // Show error state if data fetch failed
  if (appContext.isSidebarOpen === formId && inspectionDataResult.status === 3) {
    return (
      <Sidebar
        initialFormikValues={initialFormikValues}
        setInitialFormikValues={setInitialFormikValues}
        formResult={inspectionBookingResult}
        formData={formData}
        handleSubmit={handleSubmit}
        validationSchema={InspectionBookingValidationSchema(t)}
        formRef={formRef}
      >
        <Alert variant="outlined" severity="error">
          {t("checks.failed_to_load_inspection", "Failed to load inspection data. Please try again.")}
        </Alert>
      </Sidebar>
    )
  }

  return (
    <Sidebar
      initialFormikValues={initialFormikValues}
      setInitialFormikValues={setInitialFormikValues}
      formResult={inspectionBookingResult}
      formData={formData}
      handleSubmit={handleSubmit}
      validationSchema={InspectionBookingValidationSchema(t)}
      formRef={formRef}
    >
      <StyledContainer>

        <InspectionInfoBox
          service={inspection}
          isEditMode={isEditMode || false}
          isCompleted={isCompleted}
        />

        <StyledFormSection>
          <BookingDateTextField />
          <RemarkTextField />
        </StyledFormSection>

        <StyledAccordion disableGutters square>
          <StyledAccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={{ color: theme.palette.text.primary }} />
            }
          >
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                color={(theme.palette as any).primary[700]}
              />
              {t("global.important_information", "Important Information")}
            </Typography>
          </StyledAccordionSummary>

          <StyledAccordionDetails>
            <Typography
              variant="bodyMedium"
              dangerouslySetInnerHTML={{
                __html: t("checks.book_inspection_desc", bookingDesc)
              }}
            />
          </StyledAccordionDetails>
        </StyledAccordion>

        {isEditMode && (
          <Alert variant="outlined" severity="info">
            <Typography
              variant="bodyMedium"
              dangerouslySetInnerHTML={{
                __html: t('checks.inspection_edit_mode_info', editBookingDesc)
              }}
            />
          </Alert>
        )}

        {!isEditMode && isCompleted && (
          <Alert variant="outlined" severity="success">
            {t('checks.new_booking_after_completion', `Previous inspection was {status}. You are now creating a new booking for the next inspection.`).replace("{status}", t(`global.${inspection?.vehicleCheckHistoryStatus?.toLowerCase()}`, t("global.unknown", "Unknown")))}
          </Alert>
        )}
      </StyledContainer>
    </Sidebar>
  )
}
